// src/helpers/tpvHelper.js

import { get, post, patch, del } from './apiHelper';

/**
 * Helper para manejar todas las operaciones relacionadas con TPV
 */

// Configuraciones TPV
export const tpvConfigurations = {
    // Obtener todas las configuraciones de una tienda
    getByTienda: async (tiendaId) => {
        return await get(`tpv/tiendas/${tiendaId}/configurations`);
    },

    // Crear nueva configuración TPV
    create: async (tiendaId, configData) => {
        return await post(`tpv/tiendas/${tiendaId}/configurations`, configData);
    },

    // Actualizar configuración TPV
    update: async (configId, configData) => {
        return await patch(`tpv/configurations/${configId}`, configData);
    },

    // Eliminar configuración TPV
    delete: async (configId) => {
        return await del(`tpv/configurations/${configId}`);
    },

    // Obtener API Key de una configuración
    getApiKey: async (configId) => {
        return await get(`tpv/configurations/${configId}/api-key`);
    },

    // Regenerar API Key
    regenerateApiKey: async (configId) => {
        return await post(`tpv/configurations/${configId}/regenerate-key`);
    },

    // Probar configuración TPV
    test: async (configId) => {
        return await post(`tpv/configurations/${configId}/test`);
    }
};

// Estadísticas TPV
export const tpvStats = {
    // Obtener estadísticas de una tienda
    getByTienda: async (tiendaId) => {
        return await get(`tpv/tiendas/${tiendaId}/stats`);
    }
};

// Webhooks de Agora
export const agoraWebhook = {
    // Simular webhook de Agora (para testing)
    simulate: async (webhookData, apiKey) => {
        return await post('webhooks/agora', webhookData, {
            headers: {
                'x-tpv-api-key': apiKey
            }
        });
    }
};

// Generación de QR
export const qrGeneration = {
    // Generar QR para acumulación de puntos
    generateAccumulationQR: async (ticketId) => {
        return await get(`tickets/${ticketId}/qr-accumulate`);
    }
};

// Validación de QR
export const qrValidation = {
    // Validar datos del QR
    validateQRData: (qrData) => {
        try {
            const parsed = JSON.parse(qrData);

            // Validar estructura requerida
            const requiredFields = ['type', 'ticketId', 'tiendaId', 'marcaId', 'timestamp', 'version'];
            const hasAllFields = requiredFields.every(field => parsed[field]);

            if (!hasAllFields) {
                return { valid: false, error: 'Estructura de QR inválida' };
            }

            // Validar tipo
            if (parsed.type !== 'ticket_accumulation') {
                return { valid: false, error: 'Tipo de QR no válido' };
            }

            // Validar timestamp (no más de 24 horas)
            const qrTimestamp = new Date(parsed.timestamp);
            const now = new Date();
            const hoursDiff = (now - qrTimestamp) / (1000 * 60 * 60);

            if (hoursDiff > 24) {
                return { valid: false, error: 'QR expirado' };
            }

            return { valid: true, data: parsed };
        } catch (error) {
            return { valid: false, error: 'QR malformado' };
        }
    }
};

// Utilidades para TPV
export const tpvUtils = {
    // Generar ID de transacción único
    generateTransactionId: () => {
        return `AGORA_${Date.now()}`;
    },

    // Formatear datos para webhook de Agora
    formatWebhookData: (qrData, transactionData) => {
        return {
            qrData: JSON.stringify(qrData),
            monto: transactionData.monto,
            montoConDescuento: transactionData.montoConDescuento || transactionData.monto,
            puntos: transactionData.puntos,
            productos: transactionData.productos || [
                {
                    nombre: "Producto de prueba",
                    cantidad: 1,
                    precio: transactionData.monto,
                    descripcion: "Transacción procesada desde TPV"
                }
            ],
            timestamp: new Date().toISOString(),
            tpvTransactionId: tpvUtils.generateTransactionId(),
            caja: transactionData.caja || "Caja 1",
            trabajador: transactionData.trabajador || "Sistema",
            metadata: {
                source: "agora",
                version: "1.0"
            }
        };
    },

    // Enmascarar API Key para mostrar
    maskApiKey: (apiKey) => {
        if (!apiKey) return '';
        return apiKey.substring(0, 8) + '••••••••••••••••••••••••';
    },

    // Validar configuración TPV
    validateConfiguration: (config) => {
        const errors = [];

        if (!config.nombre || config.nombre.trim() === '') {
            errors.push('El nombre es obligatorio');
        }

        if (!config.tipoSoftware || config.tipoSoftware.trim() === '') {
            errors.push('El tipo de software es obligatorio');
        }

        if (config.ipAddress && !tpvUtils.isValidIP(config.ipAddress)) {
            errors.push('La dirección IP no es válida');
        }

        return {
            valid: errors.length === 0,
            errors
        };
    },

    // Validar dirección IP
    isValidIP: (ip) => {
        const ipRegex = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
        return ipRegex.test(ip);
    }
};

// Constantes para TPV
export const TPV_CONSTANTS = {
    QR_TYPES: {
        TICKET_ACCUMULATION: 'ticket_accumulation'
    },

    SOFTWARE_TYPES: {
        AGORA: 'agora',
        OTHER: 'otro'
    },

    QR_VERSION: '1.0',
    QR_EXPIRY_HOURS: 24,

    WEBHOOK_HEADERS: {
        API_KEY: 'x-tpv-api-key',
        CONTENT_TYPE: 'Content-Type'
    }
};

export default {
    tpvConfigurations,
    tpvStats,
    agoraWebhook,
    qrGeneration,
    qrValidation,
    tpvUtils,
    TPV_CONSTANTS
};



